'use client'

import { useState, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import BlogsGrid from '@/components/Blog/BlogsGrid'
import Pagination from '@/components/Pagination'
import type { PostMeta } from '@/lib/content'

interface PaginatedBlogsProps {
    initialBlogs: PostMeta[]
    category: string
    initialPagination: {
        currentPage: number
        totalPages: number
        totalBlogs: number
        blogsPerPage: number
        hasNextPage: boolean
        hasPrevPage: boolean
    }
}

const PaginatedBlogs = ({ initialBlogs, category, initialPagination }: PaginatedBlogsProps) => {
    const router = useRouter()
    const searchParams = useSearchParams()
    const [blogs, setBlogs] = useState(initialBlogs)
    const [pagination, setPagination] = useState(initialPagination)
    const [loading, setLoading] = useState(false)

    const handlePageChange = async (page: number) => {
        // Client-side validation: prevent invalid page requests
        if (page === pagination.currentPage) return
        if (page < 1 || page > pagination.totalPages) return

        setLoading(true)

        try {
            const response = await fetch(`/api/blogs?category=${category}&page=${page}`)
            if (!response.ok) {
                throw new Error('Failed to fetch blogs')
            }

            const data = await response.json()
            setBlogs(data.blogs)
            setPagination(data.pagination)

            // Update URL without refreshing the page
            const params = new URLSearchParams(searchParams.toString())
            if (page === 1) {
                params.delete('page')
            } else {
                params.set('page', page.toString())
            }

            const newUrl = params.toString() ? `?${params.toString()}` : ''
            router.push(`/categories/${category}${newUrl}`, { scroll: false })

            // Scroll to top of blog grid
            window.scrollTo({ top: 0, behavior: 'smooth' })

        } catch (error) {
            console.error('Error fetching blogs:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="relative pb-2">
            {loading && (
                <div className="absolute inset-0 bg-dark/50 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
                        <span className="text-light/80 text-sm font-medium">Loading blogs...</span>
                    </div>
                </div>
            )}

            <BlogsGrid blogs={blogs} />

            <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                className="mb-4"
            />

            <div className="text-center text-sm text-light/60 font-medium">
                Showing {((pagination.currentPage - 1) * pagination.blogsPerPage) + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.blogsPerPage, pagination.totalBlogs)} of{' '}
                {pagination.totalBlogs} blogs
            </div>
        </div>
    )
}

export default PaginatedBlogs
